import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react'
import type { AppConfig } from './config'
import { AppProvider } from './AppContext'
import { Layout } from './components/Layout'
import { ErrorBoundary } from './components/ErrorBoundary'
import { BrandLockup } from './components/BrandLockup'
import { EnvironmentsList } from './pages/EnvironmentsList'
import { EnvironmentDetail } from './pages/EnvironmentDetail'
import { CustomersList } from './pages/CustomersList'
import { CustomerDetail } from './pages/CustomerDetail'
import { OnboardCustomer } from './pages/onboarding/OnboardCustomer'
import { Activity } from './pages/Activity'

export function App({ config }: { config: AppConfig }) {
  const { instance } = useMsal()

  return (
    <>
      <UnauthenticatedTemplate>
        <div className="signin">
          <div className="signin-card">
            {/* Deduped against `Layout.tsx`, which carried a byte-identical
                copy of this lockup. The name is styled, never replaced. */}
            <BrandLockup on="light" />
            <p>Internal Rubix operations. Sign in with your Rubix account.</p>
            <button
              className="btn-primary"
              onClick={() => instance.loginRedirect({ scopes: [config.apiScope] })}
            >
              Sign in
            </button>
          </div>
        </div>
      </UnauthenticatedTemplate>

      <AuthenticatedTemplate>
        <AppProvider config={config}>
          {/* Hotfix 053: without this, one render exception blanked the whole
              console with no message — which an operator cannot distinguish
              from the service being down. */}
          <ErrorBoundary>
          <Routes>
            <Route element={<Layout />}>
              <Route index element={<Navigate to="/environments" replace />} />
              <Route path="/environments" element={<EnvironmentsList />} />
              <Route path="/environments/:cei" element={<EnvironmentDetail />} />
              <Route path="/customers" element={<CustomersList />} />
              {/* ATC-133. A ROUTE, not a modal: a 560px modal cannot hold a
                  review table and a receipt, and a modal implies
                  dismissability at exactly the moment dismissal is most
                  expensive. Declared BEFORE /customers/:accountId so the
                  literal segment is not captured as an account id. */}
              <Route path="/customers/onboard" element={<OnboardCustomer />} />
              <Route path="/customers/:accountId" element={<CustomerDetail />} />
              {/* ATC-130. A REDIRECT, not a deletion. The `*` catch-all below
                  would otherwise swallow a stale `/accounts` bookmark and land
                  the operator on Environments with no indication the page had
                  moved. */}
              <Route path="/accounts" element={<Navigate to="/customers" replace />} />
              <Route path="/activity" element={<Activity />} />
              <Route path="*" element={<Navigate to="/environments" replace />} />
            </Route>
          </Routes>
          </ErrorBoundary>
        </AppProvider>
      </AuthenticatedTemplate>
    </>
  )
}
